import React, { useEffect, useState, useCallback, Fragment } from 'react';
import apiClient from '../api/axios';
import { Container, Row, Col, Card, Table, Spinner, Alert, Dropdown, ListGroup, Pagination } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { formatUtcToLocal } from '../utils/dateUtils';

import ConfirmationModal from '../components/ConfirmationModal';
import CreateOrganizationModal from '../components/CreateOrganizationModal';
import EditOrganizationModal from '../components/EditOrganizationModal';
import OrganizationLimitsModal from '../components/OrganizationLimitsModal';

// Custom toggle component for the dropdown to use an icon
const CustomToggle = React.forwardRef(({ children, onClick }, ref) => (
    <button
      type="button"
      ref={ref}
      onClick={(e) => {
        e.preventDefault();
        onClick(e);
      }}
      className="text-dark p-2"
      style={{ background: 'none', border: 'none' }}
    >
      {children}
    </button>
));

const AdminOrganizations = () => {
    const [organizations, setOrganizations] = useState([]);
    const [totalOrganizations, setTotalOrganizations] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const ITEMS_PER_PAGE = 20;

    const [showCreateOrganizationModal, setShowCreateOrganizationModal] = useState(false);

    // State for edit organization modal
    const [showEditOrganizationModal, setShowEditOrganizationModal] = useState(false);
    const [editingOrganization, setEditingOrganization] = useState(null);

    // State for organization deletion confirmation modal
    const [showConfirmDeleteOrgModal, setShowConfirmDeleteOrgModal] = useState(false);
    const [orgToDelete, setOrgToDelete] = useState(null);

    const [showLimitsModal, setShowLimitsModal] = useState(false);
    const [selectedOrgForLimits, setSelectedOrgForLimits] = useState(null);

    const fetchOrganizations = useCallback(async (page) => {
        setLoading(true);
        setError(null);
        try {
            const skip = (page - 1) * ITEMS_PER_PAGE;
            const limit = ITEMS_PER_PAGE;
            const orgsResponse = await apiClient.get(`/admin/organizations/?skip=${skip}&limit=${limit}`);
            setOrganizations(orgsResponse.data.organizations);
            setTotalOrganizations(orgsResponse.data.total_count);
            setTotalPages(Math.ceil(orgsResponse.data.total_count / ITEMS_PER_PAGE));
            setLoading(false);
        } catch (err) {
            setError('Не удалось загрузить данные организаций.');
            setLoading(false);
            console.error(err);
        }
    }, [ITEMS_PER_PAGE]);

    useEffect(() => {
        fetchOrganizations(currentPage);
    }, [fetchOrganizations, currentPage]);

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    const renderPagination = () => {
        if (totalPages <= 1) return null;

        let items = [];
        for (let number = 1; number <= totalPages; number++) {
            items.push(
                <Pagination.Item key={number} active={number === currentPage} onClick={() => handlePageChange(number)}>
                    {number}
                </Pagination.Item>,
            );
        }
        return <Pagination className="justify-content-center mt-4">{items}</Pagination>;
    };

    const handleDeleteOrgClick = (org) => {
        setOrgToDelete(org);
        setShowConfirmDeleteOrgModal(true);
    };

    const handleConfirmDeleteOrg = async () => {
        if (orgToDelete) {
            try {
                await apiClient.delete(`/organizations/${orgToDelete.id}`);
                toast.success(`Организация ${orgToDelete.name} успешно удалена!`);
                fetchOrganizations(); // Refresh all data
            } catch (err) {
                toast.error(err.response?.data?.detail || 'Ошибка при удалении организации.');
                console.error('Delete organization error:', err);
            } finally {
                setShowConfirmDeleteOrgModal(false);
                setOrgToDelete(null);
            }
        }
    };

    const handleCancelDeleteOrg = () => {
        setShowConfirmDeleteOrgModal(false);
        setOrgToDelete(null);
    };

    const handleLimitsClick = (org) => {
        setSelectedOrgForLimits(org);
        setShowLimitsModal(true);
    };

    const handleCloseLimitsModal = () => {
        setShowLimitsModal(false);
        setSelectedOrgForLimits(null);
    };

    const handleEditOrganizationClick = (org) => {
        setEditingOrganization(org);
        setShowEditOrganizationModal(true);
    };

    const handleCloseEditOrganizationModal = () => {
        setShowEditOrganizationModal(false);
        setEditingOrganization(null);
    };

    const handleOrganizationUpdated = () => {
        fetchOrganizations(); // Refresh data after update
    };

    const bytesToMB = (bytes) => {
        if (typeof bytes !== 'number') return 'N/A';
        return (bytes / (1024 * 1024)).toFixed(2);
    };

    const formatBytes = (bytes, decimals = 2) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const dm = decimals < 0 ? 0 : decimals;
        const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
    }

    if (loading) {
        return (
            <Container className="mt-5 text-center">
                <Spinner animation="border" role="status">
                    <span className="visually-hidden">Загрузка...</span>
                </Spinner>
            </Container>
        );
    }

    if (error) {
        return (
            <Container className="mt-5">
                <Alert variant="danger">{error}</Alert>
            </Container>
        );
    }

    return (
        <Fragment>
            <Container className="mt-5">
                <h1 className="mb-4">Управление организациями</h1>

                <Row>
                    <Col md={12} className="mb-4">
                        <Card>
                            <Card.Header className="d-flex justify-content-between align-items-center">
                                <div>
                                    Организации ({totalOrganizations})
                                </div>
                                <button className="btn btn-success btn-sm" onClick={() => setShowCreateOrganizationModal(true)}>
                                    <i className="fas fa-plus"></i> Создать организацию
                                </button>
                            </Card.Header>
                            <Card.Body>
                                {organizations.length > 0 ? (
                                    <ListGroup variant="flush">
                                        {organizations.map(org => (
                                            <ListGroup.Item key={org.id} className="py-3">
                                                <div className="d-flex justify-content-between align-items-center">
                                                    <div>
                                                        <Link to={`/admin/organizations/${org.id}`} className="btn btn-link p-0" style={{ textTransform: 'none' }}>
                                                        {org.name}
                                                    </Link>
                                                        <p className="mb-1 text-muted">{org.description || 'Без описания'}</p>
                                                        <small className="text-muted">
                                                            Файлы: {bytesToMB(org.storage_used)} / {bytesToMB(org.storage_limit_bytes)} MB
                                                            <br />
                                                            Создана: {formatUtcToLocal(org.created_at)} | Изменена: {formatUtcToLocal(org.updated_at)}
                                                        </small>
                                                    </div>
                                                    <Dropdown>
                                                        <Dropdown.Toggle as={CustomToggle} id={`dropdown-org-actions-${org.id}`}>
                                                            <i className="fas fa-ellipsis-v"></i>
                                                        </Dropdown.Toggle>
                                                        <Dropdown.Menu align="end">
                                                            <Dropdown.Item
                                                                onClick={() => handleEditOrganizationClick(org)}
                                                            >
                                                                Редактировать
                                                            </Dropdown.Item>
                                                            <Dropdown.Item
                                                                onClick={() => handleLimitsClick(org)}
                                                            >
                                                                Лимиты
                                                            </Dropdown.Item>
                                                            <Dropdown.Item
                                                                onClick={() => handleDeleteOrgClick(org)}
                                                                className="text-danger"
                                                            >
                                                                Удалить
                                                            </Dropdown.Item>
                                                        </Dropdown.Menu>
                                                    </Dropdown>
                                                </div>
                                            </ListGroup.Item>
                                        ))}
                                    </ListGroup>
                                ) : (
                                    <div className="text-center p-4">Организаций пока нет.</div>
                                )}
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            </Container>

            <ConfirmationModal
                show={showConfirmDeleteOrgModal}
                title="Подтвердить удаление организации"
                message={`Вы уверены, что хотите удалить организацию "${orgToDelete?.name}"? Это действие необратимо и приведет к удалению ВСЕХ связанных данных (событий, песен, ролей и т.д.).`}
                onConfirm={handleConfirmDeleteOrg}
                onCancel={handleCancelDeleteOrg}
                confirmText="Удалить"
                cancelText="Отмена"
                variant="danger"
            />

            <CreateOrganizationModal
                show={showCreateOrganizationModal}
                onHide={() => setShowCreateOrganizationModal(false)}
                onSuccess={() => {
                    fetchOrganizations(); // Refresh the organization list
                }}
            />

            <EditOrganizationModal
                show={showEditOrganizationModal}
                onHide={handleCloseEditOrganizationModal}
                organization={editingOrganization}
                onOrganizationUpdated={handleOrganizationUpdated}
            />

            <OrganizationLimitsModal
                show={showLimitsModal}
                onHide={handleCloseLimitsModal}
                organization={selectedOrgForLimits}
                onLimitsUpdated={handleOrganizationUpdated}
            />
            {renderPagination()}
        </Fragment>
    );
};

export default AdminOrganizations;
